import { create } from 'zustand';
import { fabric } from 'fabric';
import { useContext } from 'react';
import { EditorContext } from './EditorContext';

// Helper function to get initial theme from localStorage
const getInitialTheme = (): boolean => {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('imageEditor-theme');
    const isDark = saved === 'dark' || (saved === null && true); // Default to dark mode
    
    // Set dark class on HTML element for Tailwind
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    return isDark;
  }
  return true;
};

interface Layer {
  id: string;
  name: string;
  type: 'image' | 'text' | 'shape';
  visible: boolean;
  locked: boolean;
  object: fabric.Object;
}

interface TextProperties {
  angle: number;
  skewX: number;
  skewY: number;
}

interface ShapeSettings {
  fillEnabled: boolean;
  fillColor: string;
  strokeEnabled: boolean;
  strokeColor: string;
  strokeWidth: number;
}

interface EditorState {
  canvas: fabric.Canvas | null;
  layers: Layer[];
  activeLayer: string | null;
  history: any[];
  currentHistoryIndex: number;
  selectedTool: string;
  zoom: number;
  background: string | null;
  selectedObject: fabric.Object | null;
  isDarkMode: boolean;
  isSaving: boolean;
  copiedTextProperties: TextProperties | null;
  shapeSettings: ShapeSettings;
  
  // Actions
  setCanvas: (canvas: fabric.Canvas | null) => void;
  addLayer: (layer: Layer) => void;
  removeLayer: (id: string) => void;
  setActiveLayer: (id: string) => void;
  updateLayer: (id: string, updates: Partial<Layer>) => void;
  addToHistory: () => void;
  undo: () => void;
  redo: () => void;
  setSelectedTool: (tool: string) => void;
  setZoom: (zoom: number) => void;
  setBackground: (background: string | null) => void;
  setSelectedObject: (object: fabric.Object | null) => void;
  bringToFront: () => void;
  toggleTheme: () => void;
  setIsSaving: (saving: boolean) => void;
  copyTextProperties: (textObject: fabric.IText) => void;
  pasteTextProperties: (textObject: fabric.IText) => void;
  setShapeSettings: (settings: Partial<ShapeSettings>) => void;
  // Artboard settings
  artboardWidth: number;
  artboardHeight: number;
  artboardBackgroundColor: string;
  artboardBackgroundImage: string | null;
  artboardName: string;
  setArtboardWidth: (width: number) => void;
  setArtboardHeight: (height: number) => void;
  setArtboardBackgroundColor: (color: string) => void;
  setArtboardBackgroundImage: (image: string | null) => void;
  setArtboardName: (name: string) => void;
  resetArtboardSettings: () => void;
}

const useEditorStore = create<EditorState>((set, get) => ({
  canvas: null,
  layers: [],
  activeLayer: null,
  history: [],
  currentHistoryIndex: -1,
  selectedTool: 'select',
  zoom: 1,
  background: null,
  selectedObject: null,
  isDarkMode: getInitialTheme(),
  isSaving: false,
  copiedTextProperties: null,
  shapeSettings: {
    fillEnabled: true,
    fillColor: '#ffffff',
    strokeEnabled: true,
    strokeColor: '#000000',
    strokeWidth: 2,
  },

  setCanvas: (canvas: fabric.Canvas | null) => {
    set({ canvas });
  },
  
  addLayer: (layer) => 
    set((state) => ({
      layers: [...state.layers, layer],
      activeLayer: layer.id
    })),
  
  removeLayer: (id) =>
    set((state) => ({
      layers: state.layers.filter((layer) => layer.id !== id),
      activeLayer: state.activeLayer === id ? null : state.activeLayer
    })),
  
  setActiveLayer: (id) => set({ activeLayer: id }),
  
  updateLayer: (id, updates) =>
    set((state) => ({
      layers: state.layers.map((layer) =>
        layer.id === id ? { ...layer, ...updates } : layer
      )
    })),
  
  addToHistory: () => {
    const { canvas } = get();
    if (!canvas) return;
    
    const currentState = canvas.toJSON();
    set((state) => ({
      history: [...state.history.slice(0, state.currentHistoryIndex + 1), currentState],
      currentHistoryIndex: state.currentHistoryIndex + 1
    }));
  },
  
  undo: () => {
    const { canvas, currentHistoryIndex, history } = get();
    if (!canvas || currentHistoryIndex <= 0) return;
    
    canvas.loadFromJSON(history[currentHistoryIndex - 1], () => {
      set({ currentHistoryIndex: currentHistoryIndex - 1 });
    });
  },
  
  redo: () => {
    const { canvas, currentHistoryIndex, history } = get();
    if (!canvas || currentHistoryIndex >= history.length - 1) return;
    
    canvas.loadFromJSON(history[currentHistoryIndex + 1], () => {
      set({ currentHistoryIndex: currentHistoryIndex + 1 });
    });
  },
  
  setSelectedTool: (tool) => set({ selectedTool: tool }),
  
  setZoom: (zoom) => set({ zoom }),
  
  setBackground: (background) => set({ background }),

  setSelectedObject: (object) => set({ selectedObject: object }),

  bringToFront: () => {
    const { canvas, selectedObject } = get();
    if (!canvas || !selectedObject) return;
    
    // Get all objects on canvas
    const objects = canvas.getObjects();
    // Find the index of the selected object
    const selectedIndex = objects.indexOf(selectedObject);
    
    // If object is not already at the front
    if (selectedIndex < objects.length - 1) {
      // Move it to the front
      canvas.bringToFront(selectedObject);
      
      // Ensure the object stays selected
      canvas.setActiveObject(selectedObject);
      
      // Update the canvas
      canvas.renderAll();
      
      // Add to history after the change
      const currentState = canvas.toJSON();
      set((state) => ({
        history: [...state.history.slice(0, state.currentHistoryIndex + 1), currentState],
        currentHistoryIndex: state.currentHistoryIndex + 1
      }));
    }
  },

  toggleTheme: () => set((state) => {
    const isDarkMode = !state.isDarkMode;
    
    // Save to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('imageEditor-theme', isDarkMode ? 'dark' : 'light');
      // Toggle dark class on HTML element for Tailwind
      if (isDarkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
    
    // Only update artboard background if it's the default color
    const defaultArtboardColor = '#ffffff'; // Always white
    const shouldUpdateArtboardColor = state.artboardBackgroundColor === (isDarkMode ? '#1a1a1a' : '#ffffff');
    
    return {
      isDarkMode,
      artboardBackgroundColor: shouldUpdateArtboardColor ? defaultArtboardColor : state.artboardBackgroundColor
    };
  }),

  setIsSaving: (saving) => set({ isSaving: saving }),

  // Artboard settings
  artboardWidth: 800,
  artboardHeight: 600,
  artboardBackgroundColor: '#ffffff', // Always start with white
  artboardBackgroundImage: null,
  artboardName: 'Untitled Artboard',
  setArtboardWidth: (width) => set({ artboardWidth: width }),
  setArtboardHeight: (height) => set({ artboardHeight: height }),
  setArtboardBackgroundColor: (color) => set({ artboardBackgroundColor: color }),
  setArtboardBackgroundImage: (image) => set({ artboardBackgroundImage: image }),
  setArtboardName: (name: string) => {
    set({ artboardName: name });
  },

  // Reset artboard settings to defaults for new artboards
  resetArtboardSettings: () => {
    set({
      artboardWidth: 800,
      artboardHeight: 600,
      artboardBackgroundColor: '#ffffff',
      artboardBackgroundImage: null,
      artboardName: 'Untitled Artboard'
    });
  },

  copyTextProperties: (textObject: fabric.IText) => {
    const properties: TextProperties = {
      angle: textObject.angle || 0,
      skewX: textObject.skewX || 0,
      skewY: textObject.skewY || 0,
    };
    set({ copiedTextProperties: properties });
  },

  pasteTextProperties: (textObject: fabric.IText) => {
    const { copiedTextProperties, canvas } = get();
    if (!copiedTextProperties || !canvas) return;

    textObject.set({
      angle: copiedTextProperties.angle,
      skewX: copiedTextProperties.skewX,
      skewY: copiedTextProperties.skewY,
    });

    // Force recalculation of dimensions
    textObject.initDimensions();
    textObject.setCoords();
    canvas.renderAll();
  },

  setShapeSettings: (settings) => 
    set((state) => ({
      shapeSettings: { ...state.shapeSettings, ...settings }
    })),
}));

export const useEditorStoreContext = () => {
  const context = useContext(EditorContext);
  if (!context) {
    throw new Error('useEditorStore must be used within an EditorProvider');
  }
  return context;
};

export default useEditorStore; 